import communityAnimatedIcon from "@ui/assets/illustrations/community-animated.webp";
import communityInactiveIcon from "@ui/assets/illustrations/community-inactive.webp";
import familyAnimatedIcon from "@ui/assets/illustrations/family-animated.webp";
import familyInactiveIcon from "@ui/assets/illustrations/family-inactive.webp";
import individualAnimatedIcon from "@ui/assets/illustrations/individual-animated.webp";
import individualInactiveIcon from "@ui/assets/illustrations/individual-inactive.webp";
import { Icon } from "@ui/components/Icon";
import { StandardButton } from "@ui/components/StandardButton";
import { tv } from "@ui/lib/tv";

export type AccountType = "family" | "community" | "individual";

const iconAssets: Record<AccountType, { inactive: { src: string; srcSet: string }; active: { src: string } }> = {
  family: {
    inactive: {
      src: familyInactiveIcon,
      srcSet: `${familyInactiveIcon} 1x`,
    },
    active: { src: familyAnimatedIcon },
  },
  community: {
    inactive: {
      src: communityInactiveIcon,
      srcSet: `${communityInactiveIcon} 1x`,
    },
    active: { src: communityAnimatedIcon },
  },
  individual: {
    inactive: {
      src: individualInactiveIcon,
      srcSet: `${individualInactiveIcon} 1x`,
    },
    active: { src: individualAnimatedIcon },
  },
};

const cardVariants = tv({
  slots: {
    root: [
      "group relative flex w-full flex-col pt-11 lg:pt-18 lg:h-lg:pt-21 lg:h-xl:pt-25",
      "outline-none lg:max-w-[340px] lg:flex-1",
    ],
    card: [
      "relative flex flex-1 flex-col overflow-visible rounded-[30px] lg:rounded-[28px]",
      // Colored border + tint are always on (no neutral/white rest state).
      "shadow-[0_3px_20px_0_rgba(0,0,0,0.07),inset_0_0_0_1px_var(--hover-border),inset_0_-1px_0_0_var(--hover-border-bottom)]",
      "transition-all duration-300 ease-out",
      // Hover/focus only add the scale-up; the colors are already on at rest.
      "[@media(hover:hover)]:lg:group-hover:scale-[1.02] [@media(hover:hover)]:lg:group-hover:rounded-[30px]",
      "group-has-focus-visible:lg:scale-[1.02] group-has-focus-visible:rounded-[30px]",
      "group-has-focus-visible:outline-2 group-has-focus-visible:outline-offset-2",
    ],
    iconWrapper: "absolute left-1/2 z-10 flex -translate-x-1/2 items-end justify-center",
    iconInactive: [
      // Instant swap (no opacity fade): the static is the animation's first frame, so hiding it the
      // moment the animated frame shows reads as the character just starting to move — no cross-fade flicker.
      "h-full w-full object-contain opacity-0",
      "[@media(hover:hover)]:opacity-100",
      "[@media(hover:hover)]:group-hover:opacity-0 group-has-focus-visible:opacity-0",
    ],
    iconActive: [
      "absolute inset-0 h-full w-full object-contain opacity-100",
      "[@media(hover:hover)]:opacity-0",
      "[@media(hover:hover)]:group-hover:opacity-100 group-has-focus-visible:opacity-100",
    ],
    contentWrapper: "flex flex-1 flex-col px-6 pt-20 pb-6",
    titleSection: "flex flex-col gap-2 text-center",
    title: [
      "font-display text-display-xs font-extrabold transition-colors duration-300 ease-out",
      "[@media(min-width:1024px)_and_(max-width:1053px)]:text-xl",
    ],
    subtitle: "text-base transition-colors duration-300 ease-out",
    divider: "my-5 h-px w-full transition-colors duration-300 ease-out",
    itemsList: "grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-1",
    itemRow: "flex items-start gap-2 text-left",
    itemIcon: "mt-0.5 shrink-0 h-4! w-4! transition-colors duration-300 ease-out",
    itemText: "text-base transition-colors duration-300 ease-out",
    buttonWrapper: "mt-auto pt-5",
    // The card's only interactive element, stretched over the card via the ::after overlay.
    // `static` beats StandardButton's tv base `relative` through tailwind-merge so the
    // `after:*` edges anchor to the `card` slot (which keeps `relative`); the per-type
    // negative `after:-top-*` (below) extends the overlay above the card edge to cover the
    // mascot overhang, mirroring each type's iconWrapper offset; `after:z-20` sits above the
    // z-10 iconWrapper so the character stays clickable. Breaks if StandardButton ever gains
    // absolutely-positioned resting chrome.
    button: "w-full static after:absolute after:inset-x-0 after:bottom-0 after:z-20",
  },
  variants: {
    type: {
      family: {
        iconWrapper: "h-28 w-28 -top-11 bottom-auto",
        button: "after:-top-11",
        card: [
          "[--hover-border:var(--color-red-100)]",
          "[--hover-border-bottom:var(--color-red-200)]",
          "bg-red-50",
          "group-has-focus-visible:outline-red-400",
        ],
        title: "text-red-700",
        subtitle: "text-red-700",
        divider: "bg-red-100",
        itemIcon: "text-red-400",
        itemText: "text-red-700",
      },
      community: {
        // Landscape char (400×300). Box height/width/offset computed (not eyeballed) so the rendered
        // character occupies the SAME vertical band as the family/individual icons: content top 58px,
        // baseline 167px below the card top, 109px tall — matching family. Wider, by design.
        iconWrapper: "h-[123px] w-[170px] -top-[51px] bottom-auto",
        button: "after:-top-[51px]",
        // Yellow shades bumped vs the green analogs (text → 900, icon → 700, borders → 200/300) for
        // readability on the light yellow tint.
        card: [
          "[--hover-border:var(--color-yellow-200)]",
          "[--hover-border-bottom:var(--color-yellow-300)]",
          "bg-yellow-50",
          "group-has-focus-visible:outline-yellow-500",
        ],
        title: "text-yellow-900",
        subtitle: "text-yellow-900",
        divider: "bg-yellow-200",
        itemIcon: "text-yellow-700",
        itemText: "text-yellow-900",
      },
      individual: {
        iconWrapper: "h-28 w-28 -top-11 bottom-auto",
        button: "after:-top-11",
        card: [
          "[--hover-border:var(--color-purple-200)]",
          "[--hover-border-bottom:var(--color-purple-300)]",
          "bg-purple-50",
          "group-has-focus-visible:outline-purple-500",
        ],
        title: "text-purple-700",
        subtitle: "text-purple-700",
        divider: "bg-purple-200",
        itemIcon: "text-purple-500",
        itemText: "text-purple-700",
      },
    },
    // Declared after `type` so its neutral overrides win the tailwind-merge for the disabled card
    // (already-owned family/individual). The character is grayed in the JSX (CSS grayscale).
    isDisabled: {
      true: {
        card: [
          "bg-white",
          "shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_0_rgba(0,0,0,0.06)]",
          "[@media(hover:hover)]:lg:group-hover:scale-100",
        ],
        title: "text-neutral-400",
        subtitle: "text-neutral-400",
        divider: "bg-neutral-200",
        itemIcon: "text-neutral-300",
        itemText: "text-neutral-400",
      },
    },
  },
});

const buttonColorMap: Record<AccountType, "red" | "yellow" | "purple"> = {
  family: "red",
  community: "yellow",
  individual: "purple",
};

export interface AccountTypeCardProps {
  type: AccountType;
  title: string;
  subtitle: string;
  /** Optional capability/benefit list. Omit to hide the list and divider. */
  items?: string[];
  buttonText: string;
  buttonTextShort?: string;
  onPress: () => void;
  /** Greys the card + character and removes all interaction — used when the user already owns this
   *  (singular) account type. */
  isDisabled?: boolean;
  className?: string;
}

export function AccountTypeCard({
  type,
  title,
  subtitle,
  items,
  buttonText,
  buttonTextShort,
  onPress,
  isDisabled = false,
  className,
}: AccountTypeCardProps) {
  const styles = cardVariants({ type, isDisabled });
  const icons = iconAssets[type];

  return (
    <div className={styles.root({ className })}>
      <div className={styles.card()}>
        <div className={styles.iconWrapper()}>
          {isDisabled ? (
            // Single greyed still (CSS grayscale) — no animated frame, no hover swap.
            <img
              src={icons.inactive.src}
              srcSet={icons.inactive.srcSet}
              alt=""
              className="h-full w-full object-contain opacity-80 grayscale"
              aria-hidden="true"
            />
          ) : (
            <>
              <img
                src={icons.inactive.src}
                srcSet={icons.inactive.srcSet}
                alt=""
                className={styles.iconInactive()}
                aria-hidden="true"
              />
              <img src={icons.active.src} alt="" className={styles.iconActive()} aria-hidden="true" loading="lazy" />
            </>
          )}
        </div>

        <div className={styles.contentWrapper()}>
          <div className={styles.titleSection()}>
            <h3 className={styles.title()}>{title}</h3>
            <p className={styles.subtitle()}>{subtitle}</p>
          </div>

          {items?.length ? (
            <>
              <div className={styles.divider()} />

              <ul className={styles.itemsList()}>
                {items.map((item) => (
                  <li key={item} className={styles.itemRow()}>
                    <Icon name="check" size="16" className={styles.itemIcon()} />
                    <span className={styles.itemText()}>{item}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <div className={styles.buttonWrapper()}>
            <StandardButton
              color={buttonColorMap[type]}
              size="44"
              className={styles.button()}
              isDisabled={isDisabled}
              onPress={onPress}
            >
              {buttonTextShort ? (
                <>
                  <span className="[@media(min-width:1024px)_and_(max-width:1053px)]:hidden">{buttonText}</span>
                  <span className="hidden [@media(min-width:1024px)_and_(max-width:1053px)]:inline">
                    {buttonTextShort}
                  </span>
                </>
              ) : (
                buttonText
              )}
            </StandardButton>
          </div>
        </div>
      </div>
    </div>
  );
}
